/**
 * Import media from a local or external drive.
 *
 * - Desktop app: lists mounted drives (live as they are plugged in or out) and
 *   folders the user picked, browses them, and uploads the selected files. The
 *   desktop app reads each upload part from disk as it is sent (see
 *   lib/desktop.ts `localFileSource`), so large camera files never sit in memory.
 * - Browser: picks a folder with the File System Access API
 *   (`showDirectoryPicker`, Chromium) or `<input webkitdirectory>` elsewhere, and
 *   lists the media files inside it (browser `File`s are read lazily too).
 *
 * Uploads go through the normal resumable upload pipeline; the uploaded copy
 * is what collaborators and the render workers use.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUp, Camera, ChevronRight, Film, Folder, FolderOpen, HardDrive, Image as ImageIcon, Music, Usb } from 'lucide-react';
import { mediaKindOfName, type DesktopDirEntry, type DesktopDirListing, type DesktopVolume, type MediaKind } from '@vidmakr/shared/desktop';
import { DESKTOP_DOWNLOAD_URL, desktop, localFileSource } from '@/lib/desktop';
import { formatBytes, timeAgo } from '@/lib/format';
import { errorMessage, toast } from '@/lib/toast';
import type { UploadInput } from '@/lib/upload';
import { Button, EmptyState, Modal, Select, Spinner, cx } from './ui';

type KindFilter = 'all' | MediaKind;

interface Row {
  key: string;
  name: string;
  /** Folder (relative path) shown under the name in the browser listing. */
  folder?: string;
  kind?: MediaKind;
  isDirectory: boolean;
  sizeBytes: number;
  modifiedAt: number;
}

const KIND_ICON: Record<MediaKind, typeof Film> = { video: Film, audio: Music, image: ImageIcon };

/** "From drive" button + import dialog. `onFiles` receives what to upload. */
export function ImportFromDriveButton({ onFiles, className }: { onFiles: (files: UploadInput[]) => void; className?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button className={className} onClick={() => setOpen(true)} title="Import media from a folder, camera card or external drive">
        <HardDrive size={16} /> From drive
      </Button>
      {open ? <DrivesDialog onClose={() => setOpen(false)} onFiles={onFiles} /> : null}
    </>
  );
}

export function DrivesDialog({ onClose, onFiles }: { onClose: () => void; onFiles: (files: UploadInput[]) => void }) {
  const [kind, setKind] = useState<KindFilter>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rows, setRows] = useState<Row[]>([]);
  const sourceFor = useRef<(key: string) => UploadInput | null>(() => null);
  const openDir = useRef<((key: string) => void) | undefined>(undefined);

  const visible = useMemo(() => rows.filter((r) => r.isDirectory || kind === 'all' || r.kind === kind), [rows, kind]);
  const files = visible.filter((r) => !r.isDirectory);
  const chosen = files.filter((r) => selected.has(r.key));
  const total = chosen.reduce((s, r) => s + r.sizeBytes, 0);

  const setListing = useCallback<ListingFn>((next, source, open) => {
    setRows(next);
    setSelected(new Set());
    sourceFor.current = source;
    openDir.current = open;
  }, []);

  const importSelected = () => {
    const inputs = chosen.map((r) => sourceFor.current(r.key)).filter((f): f is UploadInput => !!f);
    if (!inputs.length) return;
    onFiles(inputs);
    toast.success(`Importing ${inputs.length} file${inputs.length === 1 ? '' : 's'} (${formatBytes(total)})`);
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Import from drive or folder"
      width={920}
      footer={
        <div className="flex w-full flex-wrap items-center gap-3">
          <span className="min-w-0 flex-1 text-sm text-muted">
            {chosen.length ? `${chosen.length} selected · ${formatBytes(total)}` : `${files.length} media file${files.length === 1 ? '' : 's'}`}
          </span>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!chosen.length} onClick={importSelected}>
            Import {chosen.length || ''}
          </Button>
        </div>
      }
    >
      <div className="flex min-h-[420px] flex-col gap-4 md:flex-row">
        {desktop ? <DesktopSource onListing={setListing} /> : <BrowserSource onListing={setListing} />}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="mb-2 flex items-center gap-2">
            <Select value={kind} onChange={(e) => setKind(e.target.value as KindFilter)} className="w-36" aria-label="File type">
              <option value="all">All media</option>
              <option value="video">Video</option>
              <option value="audio">Audio</option>
              <option value="image">Images</option>
            </Select>
            <div className="flex-1" />
            <Button
              size="sm"
              variant="ghost"
              disabled={!files.length}
              onClick={() => setSelected(chosen.length === files.length ? new Set() : new Set(files.map((r) => r.key)))}
            >
              {chosen.length === files.length && files.length ? 'Select none' : 'Select all'}
            </Button>
          </div>
          <FileTable rows={visible} selected={selected} onToggle={(key) => setSelected((s) => toggle(s, key))} onOpen={(r) => openDir.current?.(r.key)} />
        </div>
      </div>
    </Modal>
  );
}

function toggle(s: Set<string>, key: string): Set<string> {
  const n = new Set(s);
  if (n.has(key)) n.delete(key);
  else n.add(key);
  return n;
}

/** Shows a listing: its rows, how to get an upload input for a file row, and how to open a directory row. */
type ListingFn = (rows: Row[], source: (key: string) => UploadInput | null, openDir?: (key: string) => void) => void;

function FileTable({ rows, selected, onToggle, onOpen }: { rows: Row[]; selected: Set<string>; onToggle: (key: string) => void; onOpen?: (row: Row) => void }) {
  if (!rows.length) {
    return (
      <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-line">
        <EmptyState icon={<Film size={22} />} title="No media here">
          Pick a drive or folder with video, audio or image files.
        </EmptyState>
      </div>
    );
  }
  return (
    <div className="max-h-[52vh] min-h-0 flex-1 overflow-auto rounded-xl border border-line">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-panel-2 text-left text-xs text-faint">
          <tr>
            <th className="w-8 px-3 py-2" />
            <th className="px-2 py-2 font-medium">Name</th>
            <th className="w-24 px-2 py-2 text-right font-medium">Size</th>
            <th className="hidden w-32 px-3 py-2 font-medium sm:table-cell">Modified</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const Icon = r.isDirectory ? Folder : KIND_ICON[r.kind ?? 'video'];
            const on = selected.has(r.key);
            return (
              <tr
                key={r.key}
                className={cx('cursor-pointer border-t border-line/60 hover:bg-panel-2', on && 'bg-accent/10')}
                onClick={() => (r.isDirectory ? onOpen?.(r) : onToggle(r.key))}
              >
                <td className="px-3 py-1.5">
                  {r.isDirectory ? null : <input type="checkbox" checked={on} onChange={() => onToggle(r.key)} onClick={(e) => e.stopPropagation()} aria-label={`Select ${r.name}`} />}
                </td>
                <td className="px-2 py-1.5">
                  <div className="flex min-w-0 items-center gap-2">
                    <Icon size={15} className={r.isDirectory ? 'shrink-0 text-accent-2' : 'shrink-0 text-muted'} />
                    <div className="min-w-0">
                      <div className="truncate">{r.name}</div>
                      {r.folder ? <div className="truncate text-xs text-faint">{r.folder}</div> : null}
                    </div>
                    {r.isDirectory ? <ChevronRight size={14} className="ml-auto shrink-0 text-faint" /> : null}
                  </div>
                </td>
                <td className="px-2 py-1.5 text-right whitespace-nowrap text-muted tabular-nums">{r.isDirectory ? '' : formatBytes(r.sizeBytes)}</td>
                <td className="hidden px-3 py-1.5 whitespace-nowrap text-muted sm:table-cell">{r.modifiedAt ? timeAgo(new Date(r.modifiedAt)) : ''}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Desktop app: drives and folders
// ---------------------------------------------------------------------------

function DesktopSource({ onListing }: { onListing: ListingFn }) {
  const bridge = desktop!;
  const [volumes, setVolumes] = useState<DesktopVolume[] | null>(null);
  const [recent, setRecent] = useState<string[]>([]);
  const [listing, setListingState] = useState<DesktopDirListing | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  /** Mount point of the drive being browsed (null for picked folders). */
  const [volume, setVolume] = useState<string | null>(null);

  const openRef = useRef<(path: string) => Promise<void>>(async () => {});
  const open = useCallback(
    async (path: string) => {
      setLoading(true);
      setError('');
      try {
        const l = await bridge.listDirectory(path);
        setListingState(l);
        const byKey = new Map<string, DesktopDirEntry>(l.entries.map((e) => [e.path, e]));
        onListing(
          l.entries.map((e) => ({ key: e.path, name: e.name, kind: e.kind, isDirectory: e.isDirectory, sizeBytes: e.sizeBytes, modifiedAt: e.modifiedAt })),
          (key) => {
            const e = byKey.get(key);
            return e && !e.isDirectory ? localFileSource(bridge, e) : null;
          },
          (key) => void openRef.current(key),
        );
        if (l.truncated) toast.info('This folder has many files; only the first 5000 are shown.');
      } catch (e) {
        setError(errorMessage(e).replace(/^Error invoking remote method '[^']+': (Error: )?/, ''));
      } finally {
        setLoading(false);
      }
    },
    [bridge, onListing],
  );
  openRef.current = open;

  useEffect(() => {
    void bridge.listVolumes().then(setVolumes);
    void bridge.recentFolders().then(setRecent);
    return bridge.onVolumesChanged((v) => {
      setVolumes(v);
    });
  }, [bridge]);

  // The drive we are browsing was unplugged.
  const root = listing?.root;
  const gone = !!volume && !!volumes && !volumes.some((v) => v.path === volume);
  useEffect(() => {
    if (gone) {
      setListingState(null);
      setVolume(null);
      onListing([], () => null);
      setError('The drive was disconnected.');
    }
  }, [gone, onListing]);

  // Camera cards (a DCIM folder at the root) open straight at their footage.
  const cards = (volumes ?? []).filter((v) => !!v.dcim);
  const drivesOnly = (volumes ?? []).filter((v) => !v.dcim);

  const pick = async () => {
    const p = await bridge.pickFolder();
    if (!p) return;
    setRecent((r) => [p, ...r.filter((x) => x !== p)].slice(0, 8));
    setVolume(null);
    void open(p);
  };

  return (
    <aside className="flex w-full shrink-0 flex-col gap-4 md:w-60">
      {cards.length ? (
        <div data-drives-cards>
          <div className="mb-1.5 text-xs font-medium tracking-wide text-faint uppercase">Import from card</div>
          <ul className="space-y-0.5">
            {cards.map((v) => (
              <SourceItem key={v.path} active={volume === v.path} icon={<Camera size={15} />} label={v.name} hint="DCIM" onClick={() => (setVolume(v.path), void open(v.dcim!))} />
            ))}
          </ul>
        </div>
      ) : null}
      <div>
        <div className="mb-1.5 text-xs font-medium tracking-wide text-faint uppercase">Drives</div>
        {volumes === null ? (
          <Spinner size={14} />
        ) : drivesOnly.length ? (
          <ul className="space-y-0.5">
            {drivesOnly.map((v) => (
              <SourceItem key={v.path} active={volume === v.path} icon={<Usb size={15} />} label={v.name} hint={v.freeBytes !== undefined ? `${formatBytes(v.freeBytes)} free` : undefined} onClick={() => (setVolume(v.path), void open(v.path))} />
            ))}
          </ul>
        ) : (
          <p className="text-xs text-faint">{cards.length ? 'No other drives.' : 'No external drives. Plug one in, or choose a folder.'}</p>
        )}
      </div>
      {recent.length ? (
        <div>
          <div className="mb-1.5 text-xs font-medium tracking-wide text-faint uppercase">Folders</div>
          <ul className="space-y-0.5">
            {recent.map((p) => (
              <SourceItem key={p} active={!volume && !!root && sameRoot(root, p)} icon={<Folder size={15} />} label={baseName(p)} hint={p} onClick={() => (setVolume(null), void open(p))} />
            ))}
          </ul>
        </div>
      ) : null}
      <Button onClick={() => void pick()}>
        <FolderOpen size={15} /> Choose folder…
      </Button>
      {listing ? (
        <div className="min-w-0 border-t border-line pt-3 text-xs">
          <div className="mb-2 truncate text-muted" title={listing.path}>
            {listing.path}
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" disabled={!listing.parent || loading} onClick={() => listing.parent && void open(listing.parent)}>
              <ArrowUp size={13} /> Up
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void bridge.revealFile(listing.path)}>
              Show in {bridge.platform === 'darwin' ? 'Finder' : 'folder'}
            </Button>
          </div>
        </div>
      ) : null}
      {loading ? <Spinner size={14} /> : null}
      {error ? <div className="rounded-lg bg-danger/10 px-3 py-2 text-xs text-danger">{error}</div> : null}
    </aside>
  );
}

function SourceItem({ icon, label, hint, active, onClick }: { icon: React.ReactNode; label: string; hint?: string; active: boolean; onClick: () => void }) {
  return (
    <li>
      <button onClick={onClick} title={hint} className={cx('flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-panel-3', active && 'bg-panel-3 text-fg')}>
        <span className="shrink-0 text-muted">{icon}</span>
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {hint && !hint.startsWith('/') && !/^[A-Z]:\\/.test(hint) ? <span className="shrink-0 text-xs text-faint">{hint}</span> : null}
      </button>
    </li>
  );
}

function baseName(p: string): string {
  return p.split(/[\\/]/).filter(Boolean).pop() ?? p;
}

function sameRoot(a: string, b: string): boolean {
  const n = (s: string) => s.replace(/[\\/]+$/, '').toLowerCase();
  return n(a) === n(b);
}

// ---------------------------------------------------------------------------
// Browser: File System Access API, or <input webkitdirectory>
// ---------------------------------------------------------------------------

interface FsHandle {
  kind: 'file' | 'directory';
  name: string;
}
interface FsFileHandle extends FsHandle {
  kind: 'file';
  getFile(): Promise<File>;
}
interface FsDirHandle extends FsHandle {
  kind: 'directory';
  values(): AsyncIterable<FsFileHandle | FsDirHandle>;
}
type DirectoryPicker = (opts?: { id?: string; mode?: 'read' }) => Promise<FsDirHandle>;

const MAX_FILES = 5000;
const MAX_DEPTH = 8;

async function walk(dir: FsDirHandle, prefix: string, out: { file: File; folder: string }[], depth: number) {
  for await (const h of dir.values()) {
    if (out.length >= MAX_FILES) return;
    if (h.name.startsWith('.')) continue;
    if (h.kind === 'directory') {
      if (depth < MAX_DEPTH) await walk(h, `${prefix}${h.name}/`, out, depth + 1);
    } else if (mediaKindOfName(h.name)) {
      out.push({ file: await h.getFile(), folder: prefix.replace(/\/$/, '') });
    }
  }
}

function BrowserSource({ onListing }: { onListing: ListingFn }) {
  const input = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const picker = (window as unknown as { showDirectoryPicker?: DirectoryPicker }).showDirectoryPicker;

  useEffect(() => {
    // React does not know the non-standard attribute.
    input.current?.setAttribute('webkitdirectory', '');
  }, []);

  const show = (folder: string, found: { file: File; folder: string }[]) => {
    setName(folder);
    const byKey = new Map(found.map((f, i) => [String(i), f.file]));
    onListing(
      found.map((f, i) => ({ key: String(i), name: f.file.name, folder: f.folder, kind: mediaKindOfName(f.file.name) ?? undefined, isDirectory: false, sizeBytes: f.file.size, modifiedAt: f.file.lastModified })),
      (key) => byKey.get(key) ?? null,
    );
    if (found.length >= MAX_FILES) toast.info(`Showing the first ${MAX_FILES} media files.`);
  };

  const choose = async () => {
    if (!picker) return input.current?.click();
    let dir: FsDirHandle;
    try {
      dir = await picker({ id: 'vidmakr-import', mode: 'read' });
    } catch {
      return; // cancelled
    }
    setBusy(true);
    try {
      const found: { file: File; folder: string }[] = [];
      await walk(dir, '', found, 0);
      show(dir.name, found);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside className="flex w-full shrink-0 flex-col gap-3 md:w-60">
      <p className="text-sm text-muted">Choose a folder, camera card or external drive. vidmakr lists the video, audio and image files inside it, including subfolders.</p>
      <Button variant="primary" loading={busy} onClick={() => void choose()}>
        <FolderOpen size={15} /> Choose folder…
      </Button>
      {name ? (
        <div className="flex items-center gap-2 truncate text-xs text-muted">
          <Folder size={13} /> {name}
        </div>
      ) : null}
      <p className="text-xs text-faint">
        Browsing drives directly, with drives showing up as you plug them in, needs the desktop app.{' '}
        <a href={DESKTOP_DOWNLOAD_URL} target="_blank" rel="noreferrer" className="underline hover:text-fg">
          Download it
        </a>
        .
      </p>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          const list = [...(e.target.files ?? [])];
          const found = list
            .filter((f) => mediaKindOfName(f.name))
            .slice(0, MAX_FILES)
            .map((f) => {
              const rel = (f as File & { webkitRelativePath?: string }).webkitRelativePath ?? '';
              return { file: f, folder: rel.split('/').slice(1, -1).join('/') };
            });
          const top = (list[0] as (File & { webkitRelativePath?: string }) | undefined)?.webkitRelativePath?.split('/')[0] ?? '';
          show(top, found);
          e.target.value = '';
        }}
      />
    </aside>
  );
}
